import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {SampleDetails} from 'constants/SampleConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ParameterStructuredTableAdapter, {
  ParameterStructuredTable,
} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import getSampleDetailsFromCell from 'notebook/sample-propagation/sample-collection/getSampleDetailsFromCell';
import {getAllSamplesByColumn} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {SampleFilter, SampleFilterType, SimpleSampleFilters} from 'notebook/sample-propagation/SampleFilter';
import getSampleId from 'notebook/sample-propagation/util/getSampleId';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {NotebookLookupConfigModalQuery} from 'notebook/structured-tables/lookup-columns/__generated/NotebookLookupConfigModalQuery';
import {NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY} from 'notebook/structured-tables/lookup-columns/runLookupConfigModalForNotebook';
import {
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableKind,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import getAddColumnToWorklistMenu, {WorklistColumn} from 'worklists/getAddColumnToWorklistMenu';
import {areIdsValidForWorklist} from 'worklists/WorklistUtil';

const BUTTON_TITLE = 'Add items to worklist';

export interface AddColumnToWorklistButtonProps {
  disabled: boolean;
  worklistColumns: Array<WorklistColumn>;
}

export function AddColumnToWorklistButton(props: AddColumnToWorklistButtonProps): JSX.Element {
  return (
    <Dropdown tooltip={BUTTON_TITLE} menu={getAddColumnToWorklistMenu({columns: props.worklistColumns})}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        className="u-mini-spacing-left"
        disabled={props.disabled}
        icon="worklist-add"
        iconAltText={BUTTON_TITLE}
        isDropdown={true}
      />
    </Dropdown>
  );
}

export function renderDropdownButton<TTableMetadata, TTableRowMetadata>({
  structuredTable,
  structuredTableAdapter,
  computedTable,
}: {
  structuredTable: StructuredTable<TTableMetadata, TTableRowMetadata>;
  structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TTableRowMetadata>;
  computedTable: Array<Array<EvaluatedCell>>;
}): JSX.Element {
  // We use the table instead of the computedTable as we only care about
  // finalized rows which have their typedValue persisted into the backing
  // table.

  const entitySampleFilters = [SimpleSampleFilters.BIOENTITY, SimpleSampleFilters.BATCH];
  // Cannot add plate wells to worklist
  const storableSampleFilters: Array<SampleFilter> = [
    SimpleSampleFilters.PLATE,
    SimpleSampleFilters.CONTAINER,
  ];

  // We only want to allow destination containers/plates as samples for transfer tables.
  // First filter out source entity / batches for the transfer table.
  const preFilters: Array<SampleFilter> =
    structuredTable.kind === StructuredTableKind.TRANSFER
      ? storableSampleFilters
      : [...entitySampleFilters, ...storableSampleFilters];

  // We need to load NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY before calling getAllSamplesByColumn because it uses the lookups from the table
  return (
    <BenchlingQuery<NotebookLookupConfigModalQuery>
      query={NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY}
      renderLoading={() => null}
    >
      {(z) => {
        const samplesByColumns = getAllSamplesByColumn(
          structuredTableAdapter.toTable(structuredTable),
          preFilters,
          computedTable,
          {
            skipLinkedSamples: false,
            removeDuplicates: true,
            includeUnfinalizedRows: true,
          }
        );

        // For Parameter Tables, we want to allow adding the Value column to a Worklist
        if (
          structuredTable.kind === StructuredTableKind.PARAMETER &&
          structuredTableAdapter.kind === StructuredTableKind.PARAMETER
        ) {
          const valueColumnIndex =
            // we know this is a ParameterStructuredTable and Adapter based on the above check
            (
              structuredTableAdapter as unknown as ParameterStructuredTableAdapter
            ).getParameterValueColumnIndex(structuredTable as unknown as ParameterStructuredTable);
          const parameterTableSamples = getBioentitySamplesFromRowDelegateColumn(
            structuredTable,
            structuredTableAdapter,
            computedTable,
            valueColumnIndex
          );
          if (parameterTableSamples.length) {
            samplesByColumns.push({
              displayName: structuredTable.columns[valueColumnIndex].name,
              sampleFilter: {type: SampleFilterType.BIOENTITY},
              samples: parameterTableSamples,
            });
          }
        }

        // Then filter out source destination columns for the transfer table as well.
        const filteredSamplesByColumns = samplesByColumns.filter(({columnDefinition}) => {
          if (structuredTable.kind === StructuredTableKind.TRANSFER) {
            return columnDefinition?.columnGroup !== TransferTableColumnGroup.SOURCE;
          }
          return true;
        });

        const worklistColumns: Array<WorklistColumn> = filteredSamplesByColumns.map(
          ({displayName, samples}) => ({
            name: displayName,
            itemIds: samples
              .map((sampleDetails) => getSampleId(sampleDetails))
              .filter((id): id is string => id != null),
          })
        );

        const hasValidOption = worklistColumns.some(
          ({itemIds}) => itemIds.length > 0 && areIdsValidForWorklist(itemIds)
        );

        return <AddColumnToWorklistButton disabled={!hasValidOption} worklistColumns={worklistColumns} />;
      }}
    </BenchlingQuery>
  );
}

/**
 * An ActionFunction button which passes the ids of all the bioentities, batches or all
 * of the storableInfos contained in a column to the AddToWorklistModal. Currently, we
 * only support adding bioentities or storableInfos which are contained
 * in the EXISTING_BIOENTITY column, CONTAINERS column or a TAG_VALUE column.  Other
 * columns can be added by including them in the ALLOWED_COLUMN_TYPES constant in
 * this file.
 */
export const addToWorklistActionFunction = makeCustomButtonActionFunction({
  getHidden: () => !BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS,
  renderToolbarButton: renderDropdownButton,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});

/**
 * Gets the bioentity samples from a ROW_DELEGATE column. This isn't included in the main
 * SamplesCollector code because we only want to allow this specifically for adding
 * items to a worklist, not for Add From Structured Table.
 */
function getBioentitySamplesFromRowDelegateColumn(
  structuredTable: UnknownStructuredTable,
  adapter: UnknownStructuredTableAdapter,
  computedTable: EvaluatedTable,
  columnIndex: number
): Array<SampleDetails> {
  const table = adapter.toTable(structuredTable);
  let samplesInColumn: Array<SampleDetails> = [];
  for (let rowIndex = 0; rowIndex < table.table.length; rowIndex++) {
    const rowColumns = adapter.getColumnsForRow(structuredTable, structuredTable.rows[rowIndex]);
    const samples = getSampleDetailsFromCell(
      computedTable[rowIndex][columnIndex],
      rowColumns[columnIndex].columnType
    );
    if (samples) {
      samplesInColumn = samplesInColumn.concat(samples);
    }
  }
  return samplesInColumn.filter((samples) => samples.bioentityInfo);
}
